import { joinURL } from 'ufo';

import { LanguageContext } from '@nzyme/i18n/LanguageContext.js';
import type { Resolved } from '@nzyme/ioc/Injectable.js';
import { defineService } from '@nzyme/ioc/Service.js';
import { createClient } from '@nzyme/rpc/createClient.js';
import { AuthStore } from '@sigma/client/auth/AuthStore.js';
import { CountryContext } from '@sigma/common/i18n/CountryContext.js';
import { API_PATH } from '@sigma/env/apps.js';
import { GUEST_ID_HEADER, GUEST_TOKEN_HEADER } from '@sigma/patient-common/constants.js';

import { AuthGuestStore } from '../auth/AuthGuestStore.js';

export type ApiClient = Resolved<typeof ApiClient>;

export type ApiClientOptions = {
    auth?: boolean | 'optional';
    authCheck?: boolean;
};

export const ApiClient = defineService({
    name: 'ApiClient',
    deps: {
        authStore: AuthStore,
        authGuestStore: AuthGuestStore,
        languageCtx: LanguageContext,
        countryCtx: CountryContext,
    },
    setup({ authStore, authGuestStore, languageCtx, countryCtx }) {
        return createClient<ApiClientOptions>({
            request: async (endpoint, options) => {
                let auth = options?.auth !== false;

                const headers: Record<string, string> = {
                    'x-language': languageCtx(),
                    'x-country': countryCtx(),
                };

                if (auth) {
                    if (options?.authCheck !== false) {
                        await authStore.checkAuth();
                    }

                    const token = authStore.auth?.idToken;
                    if (token) {
                        headers.authorization = `Bearer ${token}`;
                    } else {
                        auth = false;
                    }
                }

                if (authGuestStore.guestId && authGuestStore.token) {
                    headers[GUEST_ID_HEADER] = authGuestStore.guestId;
                    headers[GUEST_TOKEN_HEADER] = authGuestStore.token;
                }

                const url = auth
                    ? // Authorized endpoint
                      joinURL(API_PATH, endpoint.name)
                    : // Unauthorized endpoint
                      joinURL(API_PATH, 'public', endpoint.name);

                return {
                    url,
                    headers,
                };
            },
        });
    },
});
